// This Source Code Form is subject to the terms of the Mozilla Public
// License, v. 2.0. If a copy of the MPL was not distributed with this
// file, You can obtain one at https://mozilla.org/MPL/2.0/.
// Minimal path router (session 25): /{lang}/ (map), /{lang}/about,
// /{lang}/attribution. History API only, no dependency. The language
// segment is the single source of truth for the UI language.

import { useCallback, useEffect, useState } from 'react';
import { isLanguageCode, setLanguage, type LanguageCode } from '../i18n';
import { DEFAULT_LANGUAGE } from '../config/site';

export type RouteName = 'map' | 'about' | 'attribution' | 'not-found';

export interface RouteState {
  language: LanguageCode;
  route: RouteName;
  path: string;
}

interface ParsedPath {
  language: LanguageCode;
  route: RouteName;
}

/**
 * Pick the language for the root path from navigator.languages, matching
 * the Caddy root-redirect logic: en only when the FIRST tag is English
 * (en, en-US, en-GB...), anything else - including no preference - falls
 * back to the default language nl.
 */
export function rootLanguage(languages: readonly string[]): LanguageCode {
  const first = (languages[0] ?? '').toLowerCase();
  return /^en(-|$)/.test(first) ? 'en' : DEFAULT_LANGUAGE;
}

/**
 * Map a pathname to {language, route}. STRICT (review fix): only
 * '/{lang}', '/{lang}/', '/{lang}/about', '/{lang}/about/' and the
 * attribution equivalents are known routes — with AT MOST one optional
 * trailing slash. Anything else — extra segments ('/nl/about/x'),
 * doubled slashes ('/nl//about', '/nl///'), unknown pages — is
 * not-found. The old version filtered empty segments, which silently
 * accepted all of those.
 */
export function parsePath(pathname: string): ParsedPath {
  // Reject doubled slashes BEFORE stripping the single optional trailing
  // slash (review fix: checking after the strip let '/nl/about//' pass
  // as About). '/nl/about//' contains '//' before the strip and is
  // rejected; '/nl/about/' has none and survives to lose its one
  // trailing slash.
  if (pathname.includes('//')) {
    return { language: DEFAULT_LANGUAGE, route: 'not-found' };
  }
  let path = pathname;
  if (path.endsWith('/') && path !== '/') {
    path = path.slice(0, -1);
  }
  const segments = path.split('/').filter(Boolean);
  const first = segments[0] ?? '';
  if (!first || !isLanguageCode(first)) {
    return { language: DEFAULT_LANGUAGE, route: 'not-found' };
  }
  const language = first;
  const page = segments[1] ?? '';
  if (page === '' || page === undefined) {
    // '/{lang}' with an optional trailing slash is the map; a path that
    // was ONLY slashes past the language ('/nl///') already failed the
    // empty-segment check above.
    return { language, route: 'map' };
  }
  // Extra segments ('/nl/about/x') are not-found.
  if (segments.length > 2) {
    return { language, route: 'not-found' };
  }
  if (page === 'about') return { language, route: 'about' };
  if (page === 'attribution') return { language, route: 'attribution' };
  return { language, route: 'not-found' };
}

/** The path for a page in a language (used for links and language switch). */
export function pagePath(language: LanguageCode, route: RouteName): string {
  if (route === 'map') return `/${language}/`;
  if (route === 'about') return `/${language}/about`;
  if (route === 'attribution') return `/${language}/attribution`;
  return `/${language}/`;
}

/**
 * Router hook: tracks the current route, navigates via the History API
 * (Back/Forward work through popstate) and keeps i18n + <html lang>
 * in sync with the language segment.
 */
export function useRouter(): {
  state: RouteState;
  navigate: (path: string) => void;
} {
  const [state, setState] = useState<RouteState>(() => {
    const parsed = parsePath(window.location.pathname);
    return { ...parsed, path: window.location.pathname };
  });

  const syncFrom = useCallback((pathname: string): void => {
    const parsed = parsePath(pathname);
    setLanguage(parsed.language);
    setState({ ...parsed, path: pathname });
  }, []);

  useEffect((): (() => void) => {
    const onPopState = (): void => {
      syncFrom(window.location.pathname);
    };
    window.addEventListener('popstate', onPopState);
    // The service worker's navigation fallback serves index.html for '/'
    // without Caddy's root redirect, so the router itself must send '/' to
    // the right language instead of rendering not-found (replaceState, not
    // pushState: the language root is where the user belongs, not a page
    // they navigated away from).
    if (window.location.pathname === '/') {
      const language = rootLanguage(window.navigator.languages);
      window.history.replaceState({}, '', `/${language}/`);
    }
    // Sync once on mount so the language of a deep link wins over the
    // i18n module's initial guess.
    syncFrom(window.location.pathname);
    return (): void => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [syncFrom]);

  const navigate = useCallback(
    (path: string): void => {
      window.history.pushState({}, '', path);
      syncFrom(path);
    },
    [syncFrom],
  );

  return { state, navigate };
}
